import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { translations } from './landingCopy';
import type { Language } from './landingCopy';

/** The options the dropdown offers, in the order they are shown. */
const USE_CASES = ['listVenue', 'artist', 'partnership', 'teamAdventure', 'customGame', 'press', 'other'] as const;
export type ContactIntent = (typeof USE_CASES)[number] | '';

const errors = {
  en: 'Your message could not be sent. Please try again, or write to info@city-seek.com.',
  de: 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut oder schreibe an info@city-seek.com.',
  tr: 'Mesajın gönderilemedi. Lütfen tekrar dene veya info@city-seek.com adresine yaz.',
};
const privacy = {
  en: 'We use your details only to answer you. Read our privacy policy.',
  de: 'Wir nutzen deine Angaben nur, um dir zu antworten. Lies unsere Datenschutzerklärung.',
  tr: 'Bilgilerini yalnızca sana yanıt vermek için kullanırız. Gizlilik politikamızı oku.',
};
/** What to expect after sending, so the form does not end in silence. */
const reply = {
  en: 'We usually reply within two working days.',
  de: 'Wir antworten meist innerhalb von zwei Werktagen.',
  tr: 'Genellikle iki iş günü içinde yanıt veriyoruz.',
};
const sentAgain = {
  en: 'Thanks, your message is on its way. We will be in touch soon.',
  de: 'Danke, deine Nachricht ist unterwegs. Wir melden uns bald.',
  tr: 'Teşekkürler, mesajın yola çıktı. Kısa süre içinde ulaşacağız.',
};
const whereLabel = { en: 'Darmstadt, Germany', de: 'Darmstadt, Deutschland', tr: 'Darmstadt, Almanya' };

export default function LandingContact({ language, intent = '' }: { language: Language; intent?: ContactIntent }) {
  const t = translations[language] as Record<string, string>;
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [useCase, setUseCase] = useState<ContactIntent>(intent);
  const selectRef = useRef<HTMLSelectElement>(null);

  /*
    Arriving from one of the business cards.

    Those cards used to open a mail client, which asks somebody to leave the
    page, find their password and compose a message from an empty window. They
    now bring the reader here with the right subject already chosen, so the only
    thing left to do is write the sentence they came to write.
  */
  useEffect(() => {
    if (!intent) return;
    setUseCase(intent);
    setStatus('idle');
    // The form is already in view by the time this runs; move focus so a
    // keyboard or screen-reader user lands on the field that just changed.
    selectRef.current?.focus({ preventScroll: true });
  }, [intent]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'sending') return;
    const form = event.currentTarget;
    const data = new FormData(form);
    if (data.get('botcheck')) return;
    setStatus('sending');
    try {
      const chosen = String(data.get('interest') || '');
      const subjectLabel = chosen ? t[`contact.form.useCase.${chosen}`] || chosen : 'General enquiry';
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: AbortSignal.timeout(15000),
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || 'c31a805a-1109-49e8-a51e-605b9d5a2194',
          name: data.get('name'), email: data.get('email'),
          subject: `CitySeek Contact: ${subjectLabel}`,
          message: data.get('message'),
          from_name: 'CitySeek Website', to_email: 'info@city-seek.com',
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        /*
          Say why in the console, not on screen.

          The form service refuses some domains outright, and the reason only
          appears in this body: "Form submissions from this domain TLD is
          blocked" is what it answers from a *.up.railway.app or other
          free-hosting address, which looks identical to a broken form from the
          outside. A visitor is told something they can act on, in their own
          language, with an email address; whoever is debugging gets the
          sentence the service actually sent.
        */
        console.error('[contact] rejected by the form service:', response.status, result?.message || result);
        throw new Error(result?.message || 'Submission failed');
      }
      setStatus('sent');
      form.reset();
      setUseCase('');
    } catch { setStatus('error'); }
  }

  return (
    <section className="seek-contact seek-shell seek-section" id="contact">
      {/*
        A second address for this section, kept for links that already point at
        #contact-us. It is taken out of the grid flow on purpose: as an ordinary
        child it filled the first column, which pushed the heading into the
        second and dropped the form onto a row of its own below.
      */}
      <span id="contact-us" className="seek-anchor" aria-hidden="true" />
      <div className="seek-contact-intro">
        <p className="seek-eyebrow">{t['nav.contact']}</p>
        <h2>{t['contact.title']}</h2>
        <p className="seek-body-large">{t['contact.subtitle']}</p>
        <ul className="seek-contact-facts">
          <li><a href="mailto:info@city-seek.com">info@city-seek.com</a></li>
          <li><span>{whereLabel[language]}</span></li>
          <li><span>{reply[language]}</span></li>
        </ul>
      </div>

      <form onSubmit={submit} className="seek-contact-form">
        <div className="seek-form-row">
          <label htmlFor="contact-name">{t['contact.form.nameLabel']}
            <input id="contact-name" name="name" autoComplete="name" required maxLength={100} />
          </label>
          <label htmlFor="contact-email">{t['contact.form.emailLabel']}
            <input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} />
          </label>
        </div>
        <label htmlFor="contact-interest">{t['contact.form.useCaseLabel']}
          <select
            id="contact-interest" name="interest" ref={selectRef}
            value={useCase} onChange={e => setUseCase(e.target.value as ContactIntent)}
          >
            <option value="">{t['contact.form.useCaseDefault']}</option>
            {USE_CASES.map(key => <option key={key} value={key}>{t[`contact.form.useCase.${key}`]}</option>)}
          </select>
        </label>
        <label htmlFor="contact-message">{t['contact.form.messageLabel']}
          <textarea id="contact-message" name="message" rows={5} required maxLength={500}
            placeholder={t['contact.form.messagePlaceholder']} />
        </label>
        <input type="checkbox" name="botcheck" tabIndex={-1} hidden aria-hidden="true" />

        <div className="seek-form-actions">
          <button className="seek-button" type="submit" disabled={status === 'sending'}>
            {t[status === 'sending' ? 'contact.form.sending' : 'contact.form.sendButton']}
          </button>
          <a className="seek-form-privacy" href="/privacy">{privacy[language]}</a>
        </div>

        <div aria-live="polite" className="seek-form-status">
          {status === 'sent' && (
            <p className="seek-form-success">{sentAgain[language]}</p>
          )}
          {status === 'error' && <p className="seek-form-error" role="alert">{errors[language]}</p>}
        </div>
      </form>
    </section>
  );
}
